import { Input, InputGroup, InputRightAddon } from '@chakra-ui/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Position } from '@texas/components/shared/autoUpdate/types';
import {
  useAutoUpdateForm,
  AutoUpdateWrapper,
} from '@texas/components/shared/autoUpdate/useAutoUpdateForm';
import {
  AutoUpdateInquiryTierPrice,
  AutoUpdateInquiryTierPriceAdditionalData,
  EMPTY_AUTO_UPDATE_INQUIRY_TIER_PRICE,
  TierPrice,
  inquiryApi,
} from '@texas/api/endpoints/inquiryApi';
import { inquiryEvents } from './events';
import { setValueAsDecimalExtender } from '../sales/shared';

interface AutoUpdateInquiryPriceProps {
  tierId: number;
  inquiryId: number;
  currentPrice: number | null;
  currencyCode: string;
  showCurrencyAddon?: boolean;
  isDisabled?: boolean;
}

export function AutoUpdateInquiryPrice({
  tierId,
  inquiryId,
  currentPrice,
  currencyCode,
  showCurrencyAddon = true,
  isDisabled,
}: AutoUpdateInquiryPriceProps) {
  const { t } = useTranslation();
  const {
    registerAutoUpdate,
    register,
    form,
    triggerSubmit,
    resetValue,
    getValues,
  } = useAutoUpdateForm<
    AutoUpdateInquiryTierPrice,
    TierPrice,
    AutoUpdateInquiryTierPriceAdditionalData
  >({
    defaultValue: { price: currentPrice ?? null },
    requestParams: [tierId, inquiryId],
    namePrefix: `inquiry-tier-${tierId}`,
    autoUpdateRequest: inquiryApi.autoUpdateTierPrice,
    fallbackMapObject: EMPTY_AUTO_UPDATE_INQUIRY_TIER_PRICE,
    additionalData: { inquiryId },
    onResponse(data) {
      const sv = data.unmodifiedServerData.price ?? null;
      if (getValues('price') !== sv) resetValue('price', sv);
      inquiryEvents.onTierPricePatched.dispatch({ tierId, price: sv });
    },
  });

  useEffect(() => {
    resetValue('price', currentPrice ?? null);
  }, [currentPrice, resetValue]);

  return (
    <form {...form}>
      <AutoUpdateWrapper
        autoUpdateProps={registerAutoUpdate}
        path="price"
        position={Position.Right}
      >
        <InputGroup size="xs">
          <Input
            type="number"
            placeholder={showCurrencyAddon ? '—' : t('inquiry.enterPrice')}
            w={showCurrencyAddon ? '80px' : '140px'}
            isDisabled={isDisabled}
            {...register('price', {
              setValueAs: (value) => setValueAsDecimalExtender(value, true),
              onBlur: triggerSubmit,
            })}
            onClick={(e) => e.stopPropagation()}
          />
          {showCurrencyAddon && (
            <InputRightAddon>{currencyCode}</InputRightAddon>
          )}
        </InputGroup>
      </AutoUpdateWrapper>
    </form>
  );
}
